import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Nav from "@/components/Nav";
import { toast } from "sonner";
import { Sparkles, ArrowRight, CheckCircle2 } from "lucide-react";

const CONFIDENCE = [
  { v: 25, label: "Guessing" }, { v: 50, label: "Fairly sure" },
  { v: 75, label: "High confidence" }, { v: 100, label: "Certain" },
];

export default function Challenge() {
  const { aid } = useParams();
  const nav = useNavigate();
  const [q, setQ] = useState(null);
  const [target, setTarget] = useState(null);
  const [reason, setReason] = useState("");
  const [selected, setSelected] = useState(null);
  const [confidence, setConfidence] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const presentedAt = useRef(Date.now());

  const load = useCallback(async () => {
    setQ(null); setSelected(null); setConfidence(null); setResult(null);
    try {
      const { data } = await api.get(`/assessment/${aid}/challenge`);
      setQ(data.question); setTarget(data.target_domain); setReason(data.reason);
      presentedAt.current = Date.now();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load challenge");
    }
  }, [aid]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (selected == null) { toast.error("Pick an option"); return; }
    if (confidence == null) { toast.error("Set your confidence"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/assessment/challenge-answer", {
        assessment_id: aid, question_id: q.question_id,
        selected_index: selected, response_time_ms: Date.now() - presentedAt.current,
        confidence,
      });
      setResult(data);
      toast.success("Profile sharpened");
    } catch { toast.error("Save failed"); }
    finally { setLoading(false); }
  };

  if (!q && !result) return <div><Nav /><div className="max-w-xl mx-auto px-5 py-20 text-slate-400">Finding your next challenge…</div></div>;

  return (
    <div><Nav />
      <div className="max-w-2xl mx-auto px-5 py-8">
        <div className="uppercase font-mono text-xs tracking-[0.24em] text-[#E0A96D] mb-2 flex items-center gap-2">
          <Sparkles className="w-4 h-4" /> Challenge Me
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl mb-6" data-testid="challenge-title">
          {reason}: <span className="text-[#E0A96D] capitalize">{target?.replace(/_/g, " ")}</span>
        </h1>

        {q && !result && (
          <div className="rounded-2xl bg-[#131E36] border border-slate-800/70 p-6 sm:p-8 fade-up" data-testid="challenge-card">
            <div className="uppercase font-mono text-xs tracking-widest text-slate-500 mb-4">
              {q.domain.replace(/_/g," ")} · difficulty {q.difficulty}/5
            </div>
            <h2 className="font-serif text-2xl sm:text-3xl leading-snug mb-6" data-testid="challenge-prompt">{q.prompt}</h2>
            <div className="space-y-2">
              {q.options.map((opt, i) => (
                <button key={i} onClick={()=>setSelected(i)}
                  className={`w-full text-left px-4 py-3.5 rounded-xl border transition-colors ${selected===i?"border-[#E0A96D] bg-[#E0A96D]/10 text-white":"border-slate-700 hover:border-slate-500 text-slate-200"}`}
                  data-testid={`challenge-option-${i}`}>
                  <span className="font-mono text-xs text-slate-500 mr-3">{String.fromCharCode(65+i)}</span>{opt}
                </button>
              ))}
            </div>
            <div className="mt-6 pt-6 border-t border-slate-800">
              <div className="uppercase font-mono text-xs tracking-widest text-[#A855F7] mb-2">Confidence</div>
              <div className="grid grid-cols-4 gap-2">
                {CONFIDENCE.map(c => (
                  <button key={c.v} onClick={()=>setConfidence(c.v)}
                    className={`px-2 py-2 rounded-lg border text-xs transition-colors ${confidence===c.v?"border-[#A855F7] bg-[#A855F7]/20 text-white":"border-slate-700 hover:border-slate-500 text-slate-300"}`}
                    data-testid={`challenge-confidence-${c.v}`}>
                    <div className="font-mono text-sm">{c.v}%</div>
                    <div className="text-[10px] text-slate-500">{c.label}</div>
                  </button>
                ))}
              </div>
            </div>
            <button onClick={submit} disabled={loading}
              className="mt-6 w-full py-3.5 rounded-xl bg-[#E0A96D] text-[#070C18] font-semibold hover:bg-[#F0C28B] disabled:opacity-60"
              data-testid="challenge-submit">
              {loading?"Scoring…":"Submit challenge"}
            </button>
          </div>
        )}

        {result && (
          <div className="rounded-2xl bg-[#131E36] border border-slate-800/70 p-6 sm:p-8 fade-up" data-testid="challenge-result">
            <CheckCircle2 className={`w-10 h-10 mb-3 ${result.correctness===false?"text-amber-400":"text-emerald-400"}`} />
            <h2 className="font-serif text-3xl mb-2">
              {result.correctness===true?"Nailed it.":result.correctness===false?"Didn't match — but noted.":"Recorded."}
            </h2>
            <p className="text-slate-400 mb-6">
              Your profile confidence is now <span className="text-white font-mono">{result.new_profile_confidence}%</span>.
            </p>
            <div className="flex flex-wrap gap-3">
              <button onClick={load} className="px-5 py-3 rounded-xl bg-[#E0A96D] text-[#070C18] font-semibold hover:bg-[#F0C28B] inline-flex items-center gap-2" data-testid="challenge-again">
                Another challenge <ArrowRight className="w-4 h-4" />
              </button>
              <button onClick={()=>nav(`/results/${aid}`)} className="px-5 py-3 rounded-xl border border-slate-700 hover:border-slate-500 text-slate-200" data-testid="challenge-back-results">
                Back to my DualQ
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
